import React from 'react';
import { FILE_TYPE_ICONS } from '../constants/fileTypeIcons';
import { formatFileSize, formatDate } from '../utils/formatters';
import { isPreviewable } from '../../../../../services/attachmentService';

/**
 * FileItem Component
 * 
 * Clean, intuitive file card with:
 * - File type icon
 * - Filename
 * - Note (italic, light grey)
 * - Size and upload date (with time if today)
 * - Version count with dropdown
 * - Compare Versions button
 * - Download and delete actions
 */
const FileItem = ({
  file,
  isVersionsExpanded,
  onToggleVersions,
  onDownload,
  onDelete,
  onPreview,
  onRestore,
  onCompare,
}) => {
  const canPreview = isPreviewable(file.type);
  const hasVersions = file.versions && file.versions.length > 1;
  const versionCount = file.versions?.length || 1;

  return (
    <div className="attachments-modal__file">
      {/* Main file card */}
      <div className="attachments-modal__file-main">
        {/* Icon */}
        <div className={`attachments-modal__file-icon attachments-modal__file-icon--${file.type}`}>
          {FILE_TYPE_ICONS[file.type] || FILE_TYPE_ICONS.default}
        </div>
        
        {/* File info */}
        <div className="attachments-modal__file-info">
          {/* Filename */}
          <span className="attachments-modal__file-name">{file.filename}</span>
          
          {/* Note (italic, light grey) */}
          {file.note && (
            <span className="attachments-modal__file-note">{file.note}</span>
          )}
          
          {/* Meta: Size • Uploaded date */}
          <div className="attachments-modal__file-meta">
            <span className="attachments-modal__file-meta-item">
              <span className="attachments-modal__file-meta-label">Size:</span>
              <span className="attachments-modal__file-meta-value">{formatFileSize(file.size)}</span>
            </span>
            <span className="attachments-modal__file-meta-separator">•</span>
            <span className="attachments-modal__file-meta-item">
              <span className="attachments-modal__file-meta-label">Uploaded:</span>
              <span className="attachments-modal__file-meta-value">{formatDate(file.uploadedAt)}</span>
            </span>
          </div>
          
          {/* Versions row */}
          {hasVersions && (
            <div className="attachments-modal__file-versions-row">
              <button 
                className="attachments-modal__file-versions-toggle"
                onClick={() => onToggleVersions(file.id)}
              >
                <span>{versionCount} versions</span>
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  className={isVersionsExpanded ? 'rotated' : ''}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              
              {canPreview && (
                <button 
                  className="attachments-modal__file-compare-btn"
                  onClick={() => {
                    // Open preview in compare mode with the first older version
                    const firstOlderVersion = file.versions?.[1];
                    if (firstOlderVersion) {
                      onCompare?.(file, firstOlderVersion);
                    } else {
                      onPreview?.(file);
                    }
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="18" rx="1" />
                    <rect x="14" y="3" width="7" height="18" rx="1" />
                  </svg>
                  Compare Versions
                </button>
              )}
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="attachments-modal__file-actions">
          {canPreview && !hasVersions && (
            <button 
              className="attachments-modal__file-action"
              title="Preview"
              onClick={() => onPreview?.(file)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          )}
          <button 
            className="attachments-modal__file-action"
            title="Download"
            onClick={() => onDownload(file)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
          <button 
            className="attachments-modal__file-action attachments-modal__file-action--delete"
            title="Delete"
            onClick={() => onDelete(file)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Version history dropdown */}
      {isVersionsExpanded && hasVersions && (
        <div className="attachments-modal__versions">
          {file.versions.slice(1).map((version, idx) => (
            <div key={version.id} className="attachments-modal__version">
              <span className="attachments-modal__version-label">
                v{file.versions.length - idx - 1}
              </span>
              <div className="attachments-modal__version-info">
                <span className="attachments-modal__version-meta">
                  {formatFileSize(version.size)} • {formatDate(version.uploadedAt)}
                </span>
                {version.note && (
                  <span className="attachments-modal__version-note">
                    {version.note}
                  </span>
                )}
              </div>
              <div className="attachments-modal__version-actions">
                <button 
                  className="attachments-modal__version-action attachments-modal__version-action--restore"
                  title="Restore this version"
                  onClick={() => onRestore?.(file, version)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                    <path d="M3 3v5h5" />
                  </svg>
                  <span>Restore</span>
                </button>
                <button 
                  className="attachments-modal__version-action"
                  title="Download this version"
                  onClick={() => onDownload(file, version.id)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
                <button 
                  className="attachments-modal__version-action attachments-modal__version-action--delete"
                  title="Delete this version"
                  onClick={() => onDelete(file, version.id)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileItem;
